import { useState, useCallback, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import axios from 'axios';
import { withRouter } from 'react-router-dom';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import PropTypes from 'prop-types';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Grid from '@mui/material/Grid';
import { headers } from '../../../actions/defaults';
import { API_URL } from '../../../environment';

const outerBoxSx = {
  padding: '30px',
  borderRadius: '8px',
};

const listItemSx = {
  marginBottom: '12px',
  padding: '16px',
  borderBottom: '1px solid #e0e0e0',
};

const gridContainerSx = {
  alignItems: 'center',
};

const gridItemSx = {
  textAlign: 'left',
};

const buttonGroupSx = theme => ({
  marginTop: theme.spacing(3),
  display: 'flex',
  justifyContent: 'flex-end',
});

const errorTextSx = theme => ({
  color: theme.palette.error.main,
  marginTop: theme.spacing(1),
});

const textFieldSx = {
  '& .MuiInputBase-input': {
    fontSize: '16px',
    color: 'black',
    '&.Mui-disabled': {
      color: 'rgba(0, 0, 0, 0.87)',
    },
  },
};

const buttonSx = theme => ({
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'none',
    color: theme.palette.primary.contrastText,
  },
  '&:focus': {
    color: theme.palette.primary.contrastText,
  },
  '&:active': {
    color: theme.palette.primary.contrastText,
  },
});

const TaxInformation = ({ clinicAdminData, history }) => {
  const [editing, setEditing] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackOpen, setSnackOpen] = useState(false);
  const [formData, setFormData] = useState({
    is_tax_exempt: null,
    gst_hst_yn: null,
    gst_hst_number: null,
    pst_number: null,
  });
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (clinicAdminData?.clinic?.id) {
      setLoading(false);
      setFormData({
        is_tax_exempt: clinicAdminData.clinic.is_tax_exempt ? 'true' : 'false',
        gst_hst_yn: clinicAdminData.clinic.gst_hst_number ? 'true' : 'false',
        gst_hst_number: clinicAdminData.clinic.gst_hst_number || '',
        pst_number: clinicAdminData.clinic.pst_number || '',
      });
    } else {
      setLoading(true);
    }
  }, [clinicAdminData]);

  const handleInputChange = useCallback(event => {
    const { name, value } = event.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value,
    }));
    setUpdating(true);
    setError(null);
    setValidationErrors(prevErrors => ({
      ...prevErrors,
      [name]: null,
    }));
  }, []);

  const handleSnackClose = (_event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setSnackOpen(false);
  };

  const validateForm = () => {
    const errors = {};
    const gstHstPattern = /^\d{9}[A-Za-z]{2}\d{4}$/i;
    if (
      formData.gst_hst_yn === 'true' &&
      formData.gst_hst_number &&
      !gstHstPattern.test(formData.gst_hst_number)
    ) {
      errors.gst_hst_number = (
        <FormattedMessage defaultMessage="Your HST or GST number must have the format 123456789RT0001" />
      );
    }

    if (formData.gst_hst_yn === 'true' && !formData.gst_hst_number) {
      errors.gst_hst_number = <FormattedMessage defaultMessage="Required" />;
    }

    if (formData.is_tax_exempt === null) {
      errors.is_tax_exempt = <FormattedMessage defaultMessage="Required" />;
    }

    if (formData.gst_hst_yn === null) {
      errors.gst_hst_yn = <FormattedMessage defaultMessage="Required" />;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (!validateForm()) {
      return;
    }
    setSubmitting(true);
    try {
      const params = {
        clinic: {
          is_tax_exempt: formData.is_tax_exempt,
          gst_hst_number:
            formData.gst_hst_yn === 'true' ? formData.gst_hst_number : null,
          pst_number:
            formData.gst_hst_yn === 'true' ? formData.pst_number : null,
        },
      };

      const response = await axios.put(
        `${API_URL}/clinic_admin/clinics`,
        params,
        headers(),
      );
      setSnackOpen(true);
      setFormData({
        is_tax_exempt: response.data.is_tax_exempt ? 'true' : 'false',
        gst_hst_yn: response.data.gst_hst_number ? 'true' : 'false',
        gst_hst_number: response.data.gst_hst_number || '',
        pst_number: response.data.pst_number || '',
      });
      setEditing(false);
      setUpdating(false);
      setError(null);
    } catch (err) {
      setError('Failed to update clinic data');
    } finally {
      setSubmitting(false);
    }
  };
  const renderListItem = (label, content) => (
    <ListItem sx={listItemSx}>
      <Grid container sx={gridContainerSx}>
        <Grid size={6} sx={gridItemSx}>
          <Typography variant="body1" color="textSecondary">
            {label}
          </Typography>
        </Grid>
        <Grid size={6} sx={gridItemSx}>
          {content}
        </Grid>
      </Grid>
    </ListItem>
  );

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Box sx={outerBoxSx}>
      <Snackbar
        open={snackOpen}
        autoHideDuration={6000}
        onClose={handleSnackClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackClose} severity="success">
          <FormattedMessage defaultMessage="Tax information updated!" />
        </Alert>
      </Snackbar>
      <Typography component="h2" variant="h6" color="primary" gutterBottom>
        <FormattedMessage defaultMessage="Tax Information" />
      </Typography>
      <form onSubmit={handleSubmit}>
        <List disablePadding>
          {renderListItem(
            <FormattedMessage defaultMessage="Are your clinical services tax exempt?" />,
            editing ? (
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  name="is_tax_exempt"
                  value={formData.is_tax_exempt}
                  onChange={handleInputChange}
                >
                  <FormControlLabel
                    value="true"
                    control={<Radio />}
                    label={<FormattedMessage defaultMessage="Yes" />}
                  />
                  <FormControlLabel
                    value="false"
                    control={<Radio />}
                    label={<FormattedMessage defaultMessage="No" />}
                  />
                </RadioGroup>
              </FormControl>
            ) : (
              <Typography>
                {formData.is_tax_exempt === 'true' ? (
                  <FormattedMessage defaultMessage="Yes" />
                ) : (
                  <FormattedMessage defaultMessage="No" />
                )}
              </Typography>
            ),
          )}
          {validationErrors.is_tax_exempt && (
            <Typography sx={errorTextSx}>
              {validationErrors.is_tax_exempt}
            </Typography>
          )}

          {renderListItem(
            <FormattedMessage defaultMessage="Are you registered to charge taxes for your services?" />,
            editing ? (
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  name="gst_hst_yn"
                  value={formData.gst_hst_yn}
                  onChange={handleInputChange}
                >
                  <FormControlLabel
                    value="true"
                    control={<Radio />}
                    label={<FormattedMessage defaultMessage="Yes" />}
                  />
                  <FormControlLabel
                    value="false"
                    control={<Radio />}
                    label={<FormattedMessage defaultMessage="No" />}
                  />
                </RadioGroup>
              </FormControl>
            ) : (
              <Typography>
                {formData.gst_hst_yn === 'true' ? (
                  <FormattedMessage defaultMessage="Yes" />
                ) : (
                  <FormattedMessage defaultMessage="No" />
                )}
              </Typography>
            ),
          )}
          {validationErrors.gst_hst_yn && (
            <Typography sx={errorTextSx}>
              {validationErrors.gst_hst_yn}
            </Typography>
          )}

          {formData.gst_hst_yn === 'true' && (
            <>
              {renderListItem(
                <FormattedMessage defaultMessage="HST or GST number (required)" />,
                <TextField
                  name="gst_hst_number"
                  value={formData.gst_hst_number}
                  onChange={handleInputChange}
                  fullWidth
                  disabled={!editing}
                  error={!!validationErrors.gst_hst_number}
                  sx={textFieldSx}
                />,
              )}
              {validationErrors.gst_hst_number && (
                <Typography sx={errorTextSx}>
                  {validationErrors.gst_hst_number}
                </Typography>
              )}

              {renderListItem(
                <FormattedMessage defaultMessage="PST, RST, or QST number (if applicable)" />,
                <TextField
                  name="pst_number"
                  value={formData.pst_number}
                  onChange={handleInputChange}
                  fullWidth
                  disabled={!editing}
                  sx={textFieldSx}
                />,
              )}
            </>
          )}
        </List>
        <Box sx={buttonGroupSx}>
          {editing ? (
            <>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={!updating || submitting}
                sx={buttonSx}
              >
                {submitting ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  <FormattedMessage defaultMessage="Save" />
                )}
              </Button>
              <Box sx={{ ml: 1 }}>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    setEditing(false);
                    setUpdating(false);
                    setValidationErrors({});
                    history.go(0);
                  }}
                  disabled={submitting}
                  sx={buttonSx}
                >
                  <FormattedMessage defaultMessage="Cancel" />
                </Button>
              </Box>
            </>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={() => setEditing(true)}
              sx={buttonSx}
            >
              <FormattedMessage defaultMessage="Edit" />
            </Button>
          )}
        </Box>
      </form>
      {error && <Typography color="error">{error}</Typography>}
    </Box>
  );
};

TaxInformation.propTypes = {
  clinicAdminData: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
};

export default withRouter(TaxInformation);
